/**
 * Derived view models shared by the web client, MCP tools and share pages, so the
 * semantics in SPEC §5.1 and §7 are computed the same way everywhere.
 */
import { addDays, dateInTz } from './dates'
import { upcomingOccurrences } from './recurrence'
import { compareDayOrder } from './order'
import { ancestorsOf, descendantsOf, topLevelOf, type ResolvedTree } from './tree'
import { displayTitle } from './links'
import { formatFieldValue, formatMoney, hasValue, resolveLabel, subtitleFor, sumAmounts, type Subtitle } from './fields'
import { isUnderTag, tagGroupValues, tagLabel } from './tags'
import { isEq } from './math'
import { itemKind, legacyKind, SYSTEM_IDS, type HouseholdSettings, type ItemSnapshot, type FieldDef, type ItemId, type SavedView, type SavedViewFilter, type UserId, type ViewGroupBy } from './types'

export interface ItemsIndex {
  items: ReadonlyMap<string, ItemSnapshot>
  tree: ResolvedTree
}

export function isVisible(idx: ItemsIndex, id: string): boolean {
  return idx.items.has(id) && !idx.tree.inTrash.has(id) && !idx.tree.inArchive.has(id)
}

export function titleOf(idx: ItemsIndex, id: string): string | undefined {
  const it = idx.items.get(id)
  return it ? displayTitle(it.title, (x) => idx.items.get(x)?.title) : undefined
}

/** "Kitchen Reno › Permits" — ancestors of `id` from the top down. */
export function breadcrumb(idx: ItemsIndex, id: string, sep = ' › '): string {
  return ancestorsOf(idx.tree, id)
    .reverse()
    .map((a) => titleOf(idx, a) || 'Untitled')
    .join(sep)
}

export function pathIds(idx: ItemsIndex, id: string): string[] {
  return ancestorsOf(idx.tree, id).reverse()
}

// ---------------------------------------------------------------------------
// Days (SPEC §7.2)

export interface DoneEntry {
  id: string
  at: number
  /** Present for a recurring item's completion log entry. */
  recurring?: boolean
}

export interface DayBucket {
  date: string
  /** Labels (no checkbox) dated this day, all-day first then by time. */
  labels: string[]
  /** Open tasks by dayOrder (plus rolled-over ones on Today with rollToToday). */
  tasks: string[]
  /** Ids rendered on Today only because of rollToToday (show "from Sat"). */
  rolled: string[]
  /** Completed on this day. */
  done: DoneEntry[]
  /** Repeating items that will land on this day later (they're dated earlier for now): shown faintly. */
  upcoming: string[]
}

export function agendaDays(idx: ItemsIndex, settings: Pick<HouseholdSettings, 'overdue' | 'leftoversLookback' | 'timezone'>, today: string, from: string, to: string): DayBucket[] {
  const buckets = new Map<string, DayBucket>()
  for (let d = from; d <= to; d = addDays(d, 1)) {
    buckets.set(d, { date: d, labels: [], tasks: [], rolled: [], done: [], upcoming: [] })
  }
  const rollToToday = settings.overdue === 'rollToToday' && today >= from && today <= to
  const lookbackFrom = addDays(today, -settings.leftoversLookback)
  const minMs = Date.parse(from + 'T00:00:00Z') - 86400000 * 2
  for (const it of idx.items.values()) {
    if (!isVisible(idx, it.id) || it.system) continue
    const kind = itemKind(it)
    if (it.date && it.date >= from && it.date <= to) {
      const b = buckets.get(it.date)!
      if (kind === 'label') b.labels.push(it.id)
      else if (it.done === false) b.tasks.push(it.id)
    } else if (rollToToday && kind === 'task' && it.done === false && it.date && it.date < today && it.date >= lookbackFrom) {
      const b = buckets.get(today)!
      b.tasks.push(it.id)
      b.rolled.push(it.id)
    }
    // (A repeating task's completions come from its log, below.)
    if (it.done === true && it.completedAt && it.completedAt >= minMs && !it.recur) {
      const d = dateInTz(it.completedAt, settings.timezone)
      buckets.get(d)?.done.push({ id: it.id, at: it.completedAt })
    }
    if (it.recur && it.date && it.done !== true) for (const d of upcomingOccurrences(it.recur, it.date, from, to)) buckets.get(d)?.upcoming.push(it.id)
    // Done for this time: faintly on the day it comes back, and the times after.
    if (it.recur && it.reopensOn && it.done === true) {
      for (const d of [it.reopensOn, ...upcomingOccurrences(it.recur, it.reopensOn, from, to)]) if (d >= from && d <= to) buckets.get(d)?.upcoming.push(it.id)
    }
    if (it.recur) {
      for (const c of it.completions) {
        if (c.at < minMs) continue
        const d = dateInTz(c.at, settings.timezone)
        buckets.get(d)?.done.push({ id: it.id, at: c.at, recurring: true })
      }
    }
  }
  const get = (id: string) => idx.items.get(id)!
  for (const b of buckets.values()) {
    b.labels.sort((x, y) => {
      const a = get(x)
      const c = get(y)
      const ta = a.time ?? ''
      const tc = c.time ?? ''
      return ta < tc ? -1 : ta > tc ? 1 : compareDayOrder(a, c)
    })
    b.tasks.sort((x, y) => {
      const a = get(x)
      const c = get(y)
      const ra = b.rolled.includes(x) ? 0 : 1
      const rc = b.rolled.includes(y) ? 0 : 1
      if (ra !== rc) return ra - rc
      return compareDayOrder(a, c)
    })
    b.done.sort((x, y) => y.at - x.at)
    b.upcoming.sort((x, y) => (get(x).time ?? '').localeCompare(get(y).time ?? '') || compareDayOrder(get(x), get(y)))
  }
  return [...buckets.values()]
}

/** Unfinished tasks dated before today within the lookback (SPEC §7.2). Events never appear. */
export function leftovers(idx: ItemsIndex, settings: Pick<HouseholdSettings, 'leftoversLookback'>, today: string): string[] {
  const from = addDays(today, -settings.leftoversLookback)
  const out: ItemSnapshot[] = []
  for (const it of idx.items.values()) {
    if (it.done !== false || !it.date || it.system) continue
    if (it.date >= today || it.date < from) continue
    if (!isVisible(idx, it.id)) continue
    out.push(it)
  }
  out.sort((a, b) => (a.date! < b.date! ? -1 : a.date! > b.date! ? 1 : compareDayOrder(a, b)))
  return out.map((i) => i.id)
}

// ---------------------------------------------------------------------------
// Cards (SPEC §7.1, §7.4)

/** Top-level card order: Inbox, other pinned, then the rest by order, Agent log last. */
export function boardCards(idx: ItemsIndex): string[] {
  const top = (idx.tree.childrenOf.get(null) ?? []).filter((id) => isVisible(idx, id))
  const inbox = top.filter((id) => id === 'inbox')
  const agentLog = top.filter((id) => id === 'agent-log')
  const cards = top.filter((id) => !SYSTEM_IDS.has(id) && !isSavedView(idx.items.get(id)) && !idx.items.get(id)?.page)
  const pinned = cards.filter((id) => idx.items.get(id)?.pinned)
  const rest = cards.filter((id) => !idx.items.get(id)?.pinned)
  // Inbox first, pinned next, the Agent log last.
  return [...inbox, ...pinned, ...rest, ...agentLog]
}

export function isSavedView(it: ItemSnapshot | undefined): boolean {
  return !!it && !!it.props.view && typeof it.props.view === 'object'
}

/** Cards of a board rooted at an item (a card pinned as a tab): its open children. */
export function rootedCards(idx: ItemsIndex, rootId: string): string[] {
  return (idx.tree.childrenOf.get(rootId) ?? []).filter((id) => isVisible(idx, id) && idx.items.get(id)?.done !== true)
}

// ---------------------------------------------------------------------------
// Pages (sidebar tree)

/** Top-level pages, in order. */
export function topPages(idx: ItemsIndex): string[] {
  return (idx.tree.childrenOf.get(null) ?? []).filter((id) => idx.items.get(id)?.page && isVisible(idx, id))
}

/** Subpages of a page, in order. */
export function childPages(idx: ItemsIndex, id: string): string[] {
  return (idx.tree.childrenOf.get(id) ?? []).filter((c) => idx.items.get(c)?.page && isVisible(idx, c))
}

/** A page's children that are subpages are listed as pages, not as checklist rows. */
function isSubpage(idx: ItemsIndex, parent: string, child: ItemSnapshot): boolean {
  return !!child.page && !!idx.items.get(parent)?.page
}

export interface Row {
  id: string
  depth: number
  /** Number of open descendants hidden beyond the depth limit (for "+N more"). */
  more: number
}

/**
 * Open rows of a card, depth-first, nested up to `maxDepth` levels. Done items are
 * collapsed (with their subtrees) into the Done dropdown.
 */
export function openRows(idx: ItemsIndex, rootId: string, maxDepth = 3): Row[] {
  const rows: Row[] = []
  // The card's own setting decides whether checked-off items are rows at all, and where.
  const style = idx.items.get(rootId)?.doneStyle ?? 'collapse'
  const walk = (parent: string, depth: number) => {
    if (depth >= maxDepth) return
    const kids = (idx.tree.childrenOf.get(parent) ?? []).filter((id) => {
      const it = idx.items.get(id)
      return !!it && isVisible(idx, id) && !isSubpage(idx, parent, it) && (style !== 'collapse' || it.done !== true)
    })
    const ordered = style === 'bottom' ? [...kids.filter((id) => idx.items.get(id)!.done !== true), ...kids.filter((id) => idx.items.get(id)!.done === true)] : kids
    for (const id of ordered) {
      const row: Row = { id, depth, more: 0 }
      rows.push(row)
      // A checked-off item's own sub-items stay folded under it.
      if (idx.items.get(id)!.done === true) continue
      if (depth + 1 >= maxDepth) row.more = countOpen(idx, id)
      else walk(id, depth + 1)
    }
  }
  walk(rootId, 0)
  return rows
}

export function countOpen(idx: ItemsIndex, id: string): number {
  let n = 0
  for (const c of idx.tree.childrenOf.get(id) ?? []) {
    const it = idx.items.get(c)
    if (!it || !isVisible(idx, c) || it.done === true || isSubpage(idx, id, it)) continue
    n += 1 + countOpen(idx, c)
  }
  return n
}

/** Done items under a card (any depth, whose ancestors inside the card are not done). */
export function doneRows(idx: ItemsIndex, rootId: string): string[] {
  const out: string[] = []
  const walk = (parent: string) => {
    for (const id of idx.tree.childrenOf.get(parent) ?? []) {
      const it = idx.items.get(id)
      if (!it || !isVisible(idx, id)) continue
      if (it.done === true) out.push(id)
      else walk(id)
    }
  }
  walk(rootId)
  return out
}

export function sortDone(idx: ItemsIndex, ids: string[], sort: 'recent' | 'original' | 'az'): string[] {
  const get = (id: string) => idx.items.get(id)!
  const copy = [...ids]
  if (sort === 'recent') copy.sort((a, b) => (get(b).completedAt ?? 0) - (get(a).completedAt ?? 0))
  else if (sort === 'az') copy.sort((a, b) => (titleOf(idx, a) ?? '').localeCompare(titleOf(idx, b) ?? ''))
  // 'original' keeps tree order.
  return copy
}

// ---------------------------------------------------------------------------
// Done log (SPEC §7.5)

export interface CompletionEntry {
  id: string
  at: number
  by: string
  date: string
}

export function completionFeed(idx: ItemsIndex, tz: string, filter: { top?: string; tag?: string; assignee?: string } = {}): CompletionEntry[] {
  const out: CompletionEntry[] = []
  for (const it of idx.items.values()) {
    if (idx.tree.inTrash.has(it.id)) continue
    if (filter.tag && !it.tags.some((t) => isUnderTag(t, filter.tag!))) continue
    if (filter.assignee && it.assignee !== filter.assignee) continue
    if (filter.top && topLevelOf(idx.tree, it.id) !== filter.top) continue
    for (const c of it.completions) out.push({ id: it.id, at: c.at, by: c.by, date: dateInTz(c.at, tz) })
  }
  out.sort((a, b) => b.at - a.at)
  return out
}

/** Top-level cards whose every task is done: 🏁 Finished: Kitchen Reno — Oct 4. */
export function finishedCards(idx: ItemsIndex): Array<{ id: string; at: number }> {
  const out: Array<{ id: string; at: number }> = []
  for (const id of idx.tree.childrenOf.get(null) ?? []) {
    if (SYSTEM_IDS.has(id) || idx.tree.inTrash.has(id)) continue
    const self = idx.items.get(id)!
    const all = [self, ...descendantsOf(idx.tree, id).map((d) => idx.items.get(d)!)].filter((i) => i && !idx.tree.inTrash.has(i.id))
    const tasks = all.filter((i) => i.done !== null && !i.recur)
    if (!tasks.length || tasks.some((t) => t.done !== true)) continue
    out.push({ id, at: Math.max(...tasks.map((t) => t.completedAt ?? 0)) })
  }
  return out.sort((a, b) => b.at - a.at)
}

// ---------------------------------------------------------------------------
// Saved views & generic filters (SPEC §5.4)

/**
 * A saved view in its current shape. Tabs made by dragging a card to the tab bar used to
 * be `{ root }`; they are simply "inside that card, open items" filters.
 */
export function normalizeView(v: SavedView): SavedView {
  const { root, ...rest } = v
  const f = normalizeFilter(root ? { done: 'open', tagMode: 'all', ...rest.filter, ancestors: [...(rest.filter?.ancestors ?? []), root] } : (rest.filter ?? {}))
  return { ...rest, ...(root ? { groupBy: rest.groupBy ?? 'card' } : {}), filter: f }
}

/** A filter in its current shape: single `ancestor` / `kind` become the list forms. */
export function normalizeFilter(f: SavedViewFilter): SavedViewFilter {
  const { ancestor, kind, ...rest } = f
  const ancestors = [...new Set([...(rest.ancestors ?? []), ...(ancestor ? [ancestor] : [])])]
  const kinds = [...new Set([...(rest.kinds ?? []), ...(kind ? [kind] : [])].map(legacyKind))]
  const out: SavedViewFilter = { ...rest }
  if (ancestors.length) out.ancestors = ancestors
  else delete out.ancestors
  // Both kinds is the same as no kind filter.
  if (kinds.length === 1) out.kinds = kinds
  else delete out.kinds
  if (!out.archived) delete out.archived
  return out
}

/**
 * The item a view shows as-is — when its only filter is "inside this item" (what dragging a
 * card to the tab bar sets up) — so the tab can show that card interactively, as on Home.
 */
export function pinnedItemOf(v: SavedView): ItemId | null {
  const n = normalizeView(v)
  const f = n.filter
  if (f.ancestors?.length !== 1 || (n.groupBy ?? 'card') !== 'card' || (n.order ?? 'custom') !== 'custom') return null
  const narrowed = !!f.tags?.length || !!f.from || !!f.to || !!f.withinDays || !!f.assignee || !!f.hasField || !!f.kinds?.length || f.done === 'done' || !!f.archived
  return narrowed ? null : f.ancestors[0]!
}

/** Shared tabs show for everyone; "Just me" tabs only for their owner. */
export function viewVisibleTo(v: SavedView, userId: string | undefined): boolean {
  return !v.owner || v.owner === userId
}

/** The date range a filter selects (rolling windows need today), or nulls for "any". */
export function filterDateRange(f: SavedViewFilter, today?: string): { from: string | null; to: string | null } {
  if (f.withinDays && today) {
    const n = Math.max(1, Math.abs(Math.round(f.withinDays)))
    return f.withinDays > 0 ? { from: today, to: addDays(today, n - 1) } : { from: addDays(today, -(n - 1)), to: today }
  }
  return { from: f.from ?? null, to: f.to ?? null }
}

export function matchesFilter(idx: ItemsIndex, it: ItemSnapshot, raw: SavedViewFilter, today?: string): boolean {
  const f = normalizeFilter(raw)
  if (!!f.archived !== idx.tree.inArchive.has(it.id)) return false
  if (f.tags?.length) {
    const has = (t: string) => it.tags.some((x) => x === t || x.startsWith(t + '/'))
    if (f.tagMode === 'any' ? !f.tags.some(has) : !f.tags.every(has)) return false
  }
  if (f.done === 'open' && it.done !== false) return false
  if (f.done === 'done' && it.done !== true) return false
  const range = filterDateRange(f, today)
  if (range.from && (!it.date || it.date < range.from)) return false
  if (range.to && (!it.date || it.date > range.to)) return false
  if (f.assignee) {
    if (f.assignee === 'none' ? !!it.assignee : it.assignee !== f.assignee) return false
  }
  if (f.ancestors?.length) {
    const up = ancestorsOf(idx.tree, it.id)
    if (!f.ancestors.some((a) => up.includes(a))) return false
  }
  if (f.hasField && !hasValue(it.props[f.hasField])) return false
  if (f.kinds?.length && !f.kinds.includes(itemKind(it))) return false
  return true
}

export function filterItems(idx: ItemsIndex, raw: SavedViewFilter, today?: string): string[] {
  const f = normalizeFilter(raw)
  const out: string[] = []
  for (const it of idx.items.values()) {
    // Archived items only when the filter asks for them (matchesFilter checks which).
    if (it.system || isSavedView(it) || idx.tree.inTrash.has(it.id)) continue
    if (matchesFilter(idx, it, f, today)) out.push(it.id)
  }
  return out
}

export interface TreeRow {
  id: string
  /** Depth below the group root (its children are 0). */
  depth: number
  /** False for ancestors shown only so the match keeps its place in the hierarchy. */
  match: boolean
}

/**
 * The part of `rootId`'s subtree that leads to matched items, in tree order: each match,
 * plus the ancestors between it and the root (as context), nested as on Home.
 */
export function filteredTree(idx: ItemsIndex, rootId: string, matched: ReadonlySet<string>): TreeRow[] {
  const rows: TreeRow[] = []
  const walk = (parent: string, depth: number): boolean => {
    let any = false
    for (const id of idx.tree.childrenOf.get(parent) ?? []) {
      // Archived items only get this far when the filter asks for them (branches without a match are dropped below).
      if (!idx.items.has(id) || idx.tree.inTrash.has(id)) continue
      const at = rows.length
      rows.push({ id, depth, match: matched.has(id) })
      const below = walk(id, depth + 1)
      if (!matched.has(id) && !below) rows.length = at
      else any = true
    }
    return any
  }
  walk(rootId, 0)
  return rows
}

/** The item `level` steps below the top (0 = its top-level card), or itself if it's higher up. */
export function ancestorAtLevel(idx: ItemsIndex, id: string, level: number): string {
  const path = [...ancestorsOf(idx.tree, id)].reverse().concat(id)
  return path[Math.min(level, path.length - 1)]!
}

export interface ViewGroup {
  key: string
  label: string
  ids: string[]
}

/**
 * Group a view's items into cards (board layout). Keys: card → top-level item id;
 * tag → tag (items can sit in several); tag:<group> → that group's tags; assignee → user id or 'none'; date → overdue/
 * today/week/later/none; field:<key> → the value (select fields keep option order).
 */
export function groupViewItems(idx: ItemsIndex, ids: string[], groupBy: ViewGroupBy, opts: { today: string; fields?: Map<string, FieldDef> }): ViewGroup[] {
  const groups = new Map<string, ViewGroup>()
  const add = (key: string, label: string, id: string) => {
    let g = groups.get(key)
    if (!g) groups.set(key, (g = { key, label, ids: [] }))
    g.ids.push(id)
  }
  const title = (id: string) => displayTitle(idx.items.get(id)?.title ?? '', (x) => idx.items.get(x)?.title) || 'Untitled'
  const weekEnd = addDays(opts.today, 7)
  for (const id of ids) {
    const it = idx.items.get(id)
    if (!it) continue
    if (groupBy === 'card') {
      const top = topLevelOf(idx.tree, id)
      add(top, title(top), id)
    } else if (groupBy === 'tag') {
      // Tags read general → specific: grouping starts at the first level (#area, #type, #favorite).
      // Fields with a value are details too: a card per one (Phone, Address).
      const tops = new Set(it.tags.map((t) => t.split('/')[0]!))
      const own = [...(opts.fields?.values() ?? [])].filter((d) => hasValue(it.props[d.key]))
      if (!tops.size && !own.length) add('', 'No details', id)
      for (const t of tops) add(t, `#${t}`, id)
      for (const d of own) add(`field:${d.key}`, `#${d.label}`, id)
    } else if (groupBy.startsWith('tag:')) {
      // One level down from a tag: a card per next part (an item in two areas sits in both).
      // What isn't under the tag at all is left out (see narrowToGroup); what's tagged with just
      // the tag itself, nothing more specific, goes in "Other".
      const p = groupBy.slice(4)
      const next = new Set<string>()
      let exact = false
      for (const t of it.tags) {
        if (t === p) exact = true
        else if (t.startsWith(p + '/')) next.add(t.slice(p.length + 1).split('/')[0]!)
      }
      if (!next.size && exact) add('', 'Other', id)
      for (const part of next) add(`${p}/${part}`, tagLabel(part), id)
    } else if (groupBy === 'assignee') {
      add(it.assignee ?? 'none', it.assignee ?? 'Both', id)
    } else if (groupBy === 'date') {
      const d = it.date
      if (!d) add('none', 'No date', id)
      else if (d < opts.today && it.done === false) add('overdue', 'Overdue', id)
      else if (d === opts.today) add('today', 'Today', id)
      else if (d > opts.today && d <= weekEnd) add('week', 'Next 7 days', id)
      else if (d > weekEnd) add('later', 'Later', id)
      else add('past', 'Earlier', id)
    } else {
      const key = groupBy.slice('field:'.length)
      const v = it.props[key]
      if (!hasValue(v)) add('', 'No value', id)
      else {
        const s = formatFieldValue(opts.fields?.get(key), v)
        add(s, s, id)
      }
    }
  }
  const out = [...groups.values()]
  const empties = new Set(['', 'none'])
  if (groupBy === 'date') {
    const rank = ['overdue', 'today', 'week', 'later', 'past', 'none']
    out.sort((a, b) => rank.indexOf(a.key) - rank.indexOf(b.key))
  } else if (groupBy.startsWith('field:')) {
    const opts2 = opts.fields?.get(groupBy.slice(6))?.options ?? []
    const r = (k: string) => (empties.has(k) ? 1e9 : opts2.includes(k) ? opts2.indexOf(k) : 1e6)
    out.sort((a, b) => r(a.key) - r(b.key) || a.label.localeCompare(b.label))
  } else if (groupBy !== 'card') {
    out.sort((a, b) => Number(empties.has(a.key)) - Number(empties.has(b.key)) || a.label.localeCompare(b.label))
  }
  return out
}

/** Grouping by a tag (`tag:area/honolulu`) narrows the tab to what's under it: just Honolulu's items, by neighborhood. */
export function narrowToGroup(idx: ItemsIndex, ids: string[], groupBy: ViewGroupBy | undefined): string[] {
  if (!groupBy?.startsWith('tag:')) return ids
  const p = groupBy.slice(4)
  return ids.filter((id) => idx.items.get(id)?.tags.some((t) => isUnderTag(t, p)))
}

/** Assignee helper: null/absent = both. */
export function isAssignedTo(it: ItemSnapshot, user: UserId): boolean {
  return !it.assignee || it.assignee === user
}

// ---------------------------------------------------------------------------
// A tab's own card order

/** `keys` in the tab's arranged order: arranged ones first, as arranged; the rest after, as they were. */
export function arrangeKeys(keys: string[], arrange: readonly string[] | undefined): string[] {
  if (!arrange?.length) return keys
  const pos = new Map(arrange.map((k, i) => [k, i]))
  const known = keys.filter((k) => pos.has(k)).sort((a, b) => pos.get(a)! - pos.get(b)!)
  return [...known, ...keys.filter((k) => !pos.has(k))]
}

/**
 * Drop card `from` before/after `target` among `shown` (one group's cards as displayed) and
 * return the tab's new `arrange` list: this group's new order, then everything else it had.
 */
export function rearrange(shown: string[], from: string, target: string, side: 'before' | 'after', arrange: readonly string[] | undefined): string[] | null {
  if (from === target || !shown.includes(from) || !shown.includes(target)) return null
  const rest = shown.filter((k) => k !== from)
  const at = rest.indexOf(target) + (side === 'after' ? 1 : 0)
  const next = [...rest.slice(0, at), from, ...rest.slice(at)]
  return [...next, ...(arrange ?? []).filter((k) => !next.includes(k))]
}

// ---------------------------------------------------------------------------
// Fields under rows (money totals on parents)

/**
 * Sum of a money field over everything inside an item (not trashed or archived), each amount
 * once: something inside whose amount adds up what's inside it is taken as is (SPEC §7.12).
 */
export function subtreeSum(idx: ItemsIndex, id: string, key: string) {
  const items: ItemSnapshot[] = []
  const visit = (d: string) => {
    if (!isVisible(idx, d)) return
    const it = idx.items.get(d)!
    items.push(it)
    if (it.calc?.[key]?.rollup && it.calc[key]!.value) return
    for (const c of idx.tree.childrenOf.get(d) ?? []) visit(c)
  }
  for (const c of idx.tree.childrenOf.get(id) ?? []) visit(c)
  return items.length ? sumAmounts(items, key) : null
}

/**
 * The small line under a row (its subtitle): its own values for the fields its card shows
 * ("$450", "Vendor: Home Depot"), its values in the tag groups shown ("Waikiki"), and on a
 * parent, the money total of what's inside it ("Total $1,240"). Plus whether its date, person and
 * repeat show. `overrides`: a tab's own choices for its cards.
 */
export function rowSubtitle(idx: ItemsIndex, id: string, registry: Map<string, FieldDef>, overrides?: Readonly<Record<string, string[]>>): Subtitle & { bits: string[] } {
  const it = idx.items.get(id)
  if (!it) return { keys: [], date: true, person: true, repeat: true, bits: [] }
  const ancestors = ancestorsOf(idx.tree, id)
    .map((a) => idx.items.get(a))
    .filter((a): a is ItemSnapshot => !!a)
  const sub = subtitleFor(ancestors, registry, overrides)
  // The money this row actually shows (its own, or a total of what's inside it).
  const kids = !!idx.tree.childrenOf.get(id)?.length
  const moneyKeys = sub.keys.filter((k) => registry.get(k)?.type === 'money' && (hasValue(it.props[k]) || (kids && !!subtreeSum(idx, id, k))))
  const bits: string[] = []
  for (const k of sub.keys) {
    if (k.startsWith('tag:')) {
      const g = k.slice(4)
      const vals = [...(it.tags.includes(g) ? [`#${g}`] : []), ...tagGroupValues(it.tags, g)]
      if (vals.length) bits.push(vals.join(', '))
      continue
    }
    const def = registry.get(k)!
    const label = resolveLabel(k, it, ancestors, def.label)
    const own = it.props[k]
    // One money field reads fine as just "$450"; with several, say which.
    const named = def.type !== 'money' || moneyKeys.length > 1
    // Worked out: its answer, with a faint = (SPEC §7.12).
    const calc = it.calc?.[k]
    const shown = calc ? `${calc.text} =` : formatFieldValue(def, own)
    if (hasValue(own)) bits.push(named ? `${label}${def.type === 'money' ? '' : ':'} ${shown}` : shown)
    // An added-up detail is its own total: no "Total" beside it.
    if (def.type === 'money' && !calc?.rollup && idx.tree.childrenOf.get(id)?.length) {
      const total = subtreeSum(idx, id, k)
      if (total) bits.push(`${named ? `${label} total` : 'Total'} ${formatMoney(total)}`)
    }
  }
  return { ...sub, bits }
}

/** Just the field and tag bits of a row's subtitle. */
export function rowFieldBits(idx: ItemsIndex, id: string, registry: Map<string, FieldDef>, overrides?: Readonly<Record<string, string[]>>): string[] {
  return rowSubtitle(idx, id, registry, overrides).bits
}

/** Fields the item's siblings have values for (not across the Inbox or top-level cards, which are unrelated). */
export function siblingFieldKeys(idx: ItemsIndex, id: string): string[] {
  const parent = idx.tree.parentOf.get(id)
  if (!parent || SYSTEM_IDS.has(parent)) return []
  const keys = new Set<string>()
  for (const s of idx.tree.childrenOf.get(parent) ?? []) {
    if (s === id || !isVisible(idx, s)) continue
    // A worked-out one isn't waiting to be typed in on the others.
    for (const [k, v] of Object.entries(idx.items.get(s)?.props ?? {})) if (k !== 'view' && hasValue(v) && !isEq(v)) keys.add(k)
  }
  return [...keys]
}
